import { Injectable } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AssetListStateService, AssetListSharedState } from './asset-list-state.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { AssetListService } from '../../../services/asset-list.service';
import { DateHelperService } from '../../../services/date-helper.service';
import { AssetListSortType, AssetListSortTypes } from '../../../models';
import { Asset, Project, AffectedAsset } from '../models/asset.model';
@Injectable({
  providedIn: 'root',
})
export class AssetDataService {
  constructor(
    private readonly stateService: AssetListStateService,
    private readonly analyticsService: AnalyticsService,
    private readonly dateHelperService: DateHelperService,
    private readonly assetListService: AssetListService
  ) {}

  public getCampaignProjects(campaignId: string, ngUnsubscribe: Subject<void>): void {
    this.analyticsService
      .getProjectsByCampaignId(campaignId)
      .pipe(takeUntil(ngUnsubscribe))
      .subscribe({
        next: (res: Project[]) => {
          const projects: Record<string, Project> = {};
          for (const project of res) {
            projects[project.id] = project;
          }
          this.stateService.updateState({ projects });
        },
      });
  }

  public getLinkedAssets(
    campaignId: string,
    projectId?: string,
    asWidget?: boolean,
    onlyShowAssetWithStatus?: boolean,
    filterBundleCallback?: () => void,
    ngUnsubscribe?: Subject<void>
  ): void {
    this.assetListService
      .getAffectedAssetByCampaignId(campaignId)
      .pipe(takeUntil(ngUnsubscribe))
      .subscribe({
        next: (res: AffectedAsset[]) => {
          const affectedAssets: Record<string, AffectedAsset> = {};

          for (const asset of res) {
            const shouldIncludeAsset: boolean = this.shouldIncludeAsset(asset, projectId, asWidget);
            if (shouldIncludeAsset) {
              affectedAssets[asset.assetListId] = asset;
              this.convertDeliveryDate(affectedAssets[asset.assetListId]);
            }
          }

          this.stateService.updateState({ affectedAssets });

          if (onlyShowAssetWithStatus && filterBundleCallback) {
            filterBundleCallback();
          }
        },
      });
  }

  private shouldIncludeAsset(asset: AffectedAsset, projectId?: string, asWidget?: boolean): boolean {
    if (projectId && asWidget) {
      return asset.projectId === projectId;
    }
    return true;
  }

  private convertDeliveryDate(asset: AffectedAsset): void {
    const { deliveryDate } = asset;
    if (deliveryDate && (typeof deliveryDate === 'string' || typeof deliveryDate === 'number')) {
      asset.deliveryDate = this.dateHelperService.fromDateToDatepicker(new Date(deliveryDate));
    }
  }

  public sortAssetBundle(bundle: Asset[], currentSort: AssetListSortTypes): Asset[] {
    if (!bundle) return bundle;

    const state: AssetListSharedState = this.stateService.currentState;

    switch (currentSort) {
      case AssetListSortType.releaseDate:
        bundle.sort((a: Asset, b: Asset) => {
          const bTime: number = b.releaseDate?.year
            ? new Date(b.releaseDate.year, b.releaseDate.month - 1, b.releaseDate.day).getTime()
            : 0;
          const aTime: number = a.releaseDate?.year
            ? new Date(a.releaseDate.year, a.releaseDate.month - 1, a.releaseDate.day).getTime()
            : 0;
          return bTime > aTime ? 1 : -1;
        });
        break;
      case AssetListSortType.status:
        bundle.sort((a: Asset, b: Asset) => {
          const aStatus: string = state.affectedAssets[a.id]?.status || '';
          const bStatus: string = state.affectedAssets[b.id]?.status || '';
          return bStatus > aStatus ? 1 : -1;
        });
        break;
      case AssetListSortType.lastUpdated:
        bundle.sort((a: Asset, b: Asset) => {
          const bTime: number = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          const aTime: number = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          return bTime > aTime ? 1 : -1;
        });
        break;
      case AssetListSortType.alphabetical:
        bundle.sort((a: Asset, b: Asset) => {
          const aValue: string = a.title || a.detail || '';
          const bValue: string = b.title || b.detail || '';
          return aValue > bValue ? 1 : -1;
        });
        break;
      default:
        break;
    }

    return bundle;
  }

  public filterBundleWithStatus(bundle: Asset[]): Asset[] {
    if (!bundle?.length) {
      return bundle;
    }
    const state: AssetListSharedState = this.stateService.currentState;
    return bundle.filter((b: Asset) => {
      return state.affectedAssets[b.id]?.status;
    });
  }

  public getAllAsset(): Asset[] {
    const state: AssetListSharedState = this.stateService.currentState;
    return structuredClone(state.allAssets);
  }
}
